import React from "react";
import Section from "../ui/Section/Section";
import { Grid } from "../ui/Grid/Grid";
import Card, {
    CardBody,
    CardTitle,
    CardUnit,
    CardValue,
} from "../ui/Card/Card";
import ChartCard from "../ui/Card/ChartCard";
import GaugeStat from "../ui/GaugeStat/GaugeStat";
import SplitBar from "../ui/SplitBar/SplitBar";
import MiniMetric from "../ui/MiniMetric/MiniMetric";
import DeltaValue from "../ui/DeltaValue/DeltaValue";
import SectionSkeleton from "../ui/SectionSkeleton/SectionSkeleton";
import { KPI, SECTIONS } from "../../constants";
import { fmt1, fmt1Signed, fmtN } from "../../functions/format";
import { pctChange } from "../../functions/mapEffectif";
import { CHART_COLORS } from "../../styles/theme";

const { id, title } = SECTIONS.taches;

const SKELETON_ROWS = [
    { cols: 3, cards: [2, 3, 3] },
    { cols: 3, cards: [3, 3, 2] },
];

/** Texte du tooltip "vs précédent" ; ajuster le suffixe ici. */
const formatPctDeltaTip = (current, previous) => {
    if (current === null || current === undefined) return undefined;
    const delta = pctChange(current, previous);
    return delta !== null ? `${fmt1Signed(delta)}% vs précédent` : undefined;
};

/** Carte "grande valeur" centrée verticalement */
const BigValueCard = ({ label, value, unit }) => (
    <Card column>
        <CardTitle>{label}</CardTitle>
        <CardBody>
            <CardValue>
                {value}
                <CardUnit>{unit}</CardUnit>
            </CardValue>
        </CardBody>
    </Card>
);

const TachesSection = ({ data, isLoading }) => {
    if (isLoading && !data) {
        return (
            <Section id={id} title={title}>
                <SectionSkeleton rows={SKELETON_ROWS} />
            </Section>
        );
    }

    if (!data) return null;

    return (
        <Section id={id} title={title}>
            <Grid cols={3}>
                <BigValueCard
                    label={KPI.absences_jour.label}
                    value={
                        <DeltaValue
                            section={id}
                            tip={formatPctDeltaTip(
                                data.absencesJour,
                                data.absencesJourPrevious
                            )}
                        >
                            {fmtN(data.absencesJour)}
                        </DeltaValue>
                    }
                    unit={KPI.absences_jour.unit}
                />

                <ChartCard title="Soldes de vacances" fill>
                    <SplitBar
                        left={{
                            label: "Consommé",
                            value: (
                                <DeltaValue
                                    section={id}
                                    tip={formatPctDeltaTip(
                                        data.vacancesAllouees,
                                        data.vacancesAlloueesPrevious
                                    )}
                                >
                                    {`${fmt1(data.vacancesAllouees)} j`}
                                </DeltaValue>
                            ),
                            pct: data.vacancesAlloueesPct,
                            tip: `Consommé : ${fmt1(
                                data.vacancesConsommees
                            )} j sur ${fmt1(data.vacancesDisponibles)} j`,
                        }}
                        right={{
                            label: "Disponible",
                            value: (
                                <DeltaValue
                                    section={id}
                                    tip={formatPctDeltaTip(
                                        data.vacancesDisponibles,
                                        data.vacancesDisponiblesPrevious
                                    )}
                                >
                                    {`${fmt1(data.vacancesDisponibles)} j`}
                                </DeltaValue>
                            ),
                            tip: `Disponible : ${fmt1(
                                data.vacancesDisponibles
                            )} j`,
                        }}
                    />
                </ChartCard>

                <ChartCard title="Mouvements en cours">
                    <MiniMetric
                        label="Onboarding"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.onboardingsEnCours,
                                    data.onboardingsEnCoursPrevious
                                )}
                            >
                                {`${fmtN(data.onboardingsEnCours)} collab.`}
                            </DeltaValue>
                        }
                    />
                    <MiniMetric
                        label="Offboarding"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.offboardingsEnCours,
                                    data.offboardingsEnCoursPrevious
                                )}
                            >
                                {`${fmtN(data.offboardingsEnCours)} collab.`}
                            </DeltaValue>
                        }
                    />
                </ChartCard>
            </Grid>

            <Grid cols={3} $mt>
                <ChartCard title="Validation feuilles de temps">
                    <GaugeStat
                        pct={data.validationFeuilles}
                        unit="%"
                        sub="validées"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.validationFeuilles,
                                    data.validationFeuillesPrevious
                                )}
                            >
                                {fmt1(data.validationFeuilles)}
                            </DeltaValue>
                        }
                        tip={`${fmt1(
                            data.validationFeuilles
                        )} % des feuilles validées`}
                    />
                </ChartCard>

                <ChartCard title="Envoi feuilles de temps">
                    <GaugeStat
                        pct={data.envoiFeuilles}
                        color={CHART_COLORS.blue}
                        unit="%"
                        sub="envoyées"
                        value={
                            <DeltaValue
                                section={id}
                                tip={formatPctDeltaTip(
                                    data.envoiFeuilles,
                                    data.envoiFeuillesPrevious
                                )}
                            >
                                {fmt1(data.envoiFeuilles)}
                            </DeltaValue>
                        }
                        tip={`${fmt1(
                            data.envoiFeuilles
                        )} % des feuilles envoyées`}
                    />
                </ChartCard>

                <BigValueCard
                    label="Tâches à échéance"
                    value={
                        <DeltaValue
                            section={id}
                            tip={formatPctDeltaTip(
                                data.tachesEchues,
                                data.tachesEchuesPrevious
                            )}
                        >
                            {fmtN(data.tachesEchues)}
                        </DeltaValue>
                    }
                    unit="tâches"
                />
            </Grid>
        </Section>
    );
};

export default TachesSection;
